import type { CapturedNetworkRequest } from 'posthog-js'

/**
 * What PostHog session replay may record, shared by the dashboard and the
 * portal. Replays are kept for 90 days and readable by everyone with access
 * to the PostHog project, so anything a payer or merchant could use to act as
 * someone else stays out of them: credentials, session and remember-me
 * tokens, one-time codes, and bank account details.
 */

const REDACTED = 'redacted'

// PostHog's own payload deny list, which a custom
// `maskCapturedNetworkRequestFn` switches off, plus the names our GraphQL
// operations give secrets and bank details. Matched as lowercase substrings
// of the whole body, so a hit redacts the body rather than one field: a body
// is opaque text here, and a partial scrub of it is easy to get wrong.
const BODY_DENY_LIST = [
  'password',
  'passwd',
  'secret',
  'token',
  'auth',
  'credential',
  'apikey',
  'api_key',
  'privatekey',
  'private_key',
  'accountnumber',
  'routingnumber',
  'encryptionkey',
  'customerkey',
  'verificationcode',
  'taxid',
  'ssn',
]

const SSN_SHAPE = /\b\d{3}-\d{2}-\d{4}\b/

// Signed links carry their credential in the query string: `?token=` on
// emailed portal links, `X-Amz-Signature` on presigned S3 document URLs.
const SENSITIVE_QUERY_PARAM = /token|signature|credential|secret|password|code/i

function scrubBody(
  body: string | null | undefined,
  label: 'Request' | 'Response',
): string | null | undefined {
  if (!body) return body
  const lower = body.toLowerCase()
  const hit = BODY_DENY_LIST.find((word) => lower.includes(word))
  if (hit) return `${label} body ${REDACTED} as it may contain: ${hit}`
  if (SSN_SHAPE.test(body)) return `${label} body ${REDACTED}`
  return body
}

function redactSensitiveQueryParams(url: string): string {
  const queryStart = url.indexOf('?')
  if (queryStart === -1) return url
  const hashStart = url.indexOf('#', queryStart)
  const end = hashStart === -1 ? url.length : hashStart
  const query = url
    .slice(queryStart + 1, end)
    .split('&')
    .map((pair) => {
      const separator = pair.indexOf('=')
      if (separator === -1) return pair
      const name = pair.slice(0, separator)
      return SENSITIVE_QUERY_PARAM.test(name) ? `${name}=${REDACTED}` : pair
    })
    .join('&')
  return `${url.slice(0, queryStart + 1)}${query}${url.slice(end)}`
}

/**
 * `session_recording.maskCapturedNetworkRequestFn`. PostHog still strips
 * the Authorization header before calling it.
 */
export function scrubReplayNetworkRequest(
  request: CapturedNetworkRequest,
): CapturedNetworkRequest {
  return {
    ...request,
    name: redactSensitiveQueryParams(request.name),
    requestBody: scrubBody(request.requestBody, 'Request'),
    responseBody: scrubBody(request.responseBody, 'Response'),
  }
}

const SENSITIVE_AUTOCOMPLETE =
  /^(one-time-code|new-password|current-password|cc-)/

const SENSITIVE_FIELD_NAME =
  /account.?number|routing|ssn|social.?security|tax.?id|\bein\b|iban|swift|ifsc/i

// Bank account, routing, card and tax id values are runs of digits however
// the field is named, so a run of five masks the value. Spaces and dashes are
// dropped first so "123-45-6789" counts; dots and commas are not, so an
// amount under $10,000 ("1500.00", "9,999.99") stays readable.
const DIGIT_RUN = /\d{5,}/

function isSensitiveInput(value: string, element?: HTMLElement): boolean {
  if (element) {
    if (element.closest('.ph-mask')) return true
    if (element instanceof HTMLInputElement && element.type === 'password') {
      return true
    }
    const autocomplete = element.getAttribute('autocomplete') ?? ''
    if (SENSITIVE_AUTOCOMPLETE.test(autocomplete)) return true
    const fieldName = `${element.getAttribute('name') ?? ''} ${element.id}`
    if (SENSITIVE_FIELD_NAME.test(fieldName)) return true
  }
  return DIGIT_RUN.test(value.replace(/[\s-]/g, ''))
}

/**
 * `session_recording.maskInputFn`, for use with `maskAllInputs: true`, under
 * which rrweb passes every input's value through it. Keeps ordinary input
 * readable in replays and masks what {@link isSensitiveInput} flags; add the
 * `ph-mask` class to an input, or an ancestor, to mask one the rules miss.
 */
export function maskSensitiveReplayInput(
  text: string,
  element?: HTMLElement,
): string {
  return isSensitiveInput(text, element) ? '*'.repeat(text.length) : text
}
